import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { footerData, brandAssets } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);
  const footerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: footerRef,
    offset: ["start end", "end end"],
  });
  const bloomY = useTransform(scrollYProgress, [0, 1], ["6%", "-10%"]);
  const geoY = useTransform(scrollYProgress, [0, 1], [24, -24]);
  const geoOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.9]);

  const videoRef = useRef(null);
  const durationRef = useRef(0);

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const vid = videoRef.current;
    if (vid && durationRef.current) {
      vid.currentTime = v * durationRef.current;
    }
  });

  const submit = (e) => {
    e.preventDefault();
    if (email.trim()) setJoined(true);
  };

  return (
    <footer
      ref={footerRef}
      data-testid="site-footer"
      className="relative overflow-hidden bg-[var(--strollee-dark)] text-[var(--strollee-bg)]"
    >
      <motion.div
        aria-hidden="true"
        style={{ y: bloomY }}
        className="strollee-bloom absolute -top-1/4 left-1/4 h-[90vh] w-[70vw] rounded-full bg-[var(--strollee-accent)] opacity-[0.17] blur-[140px]"
      />
      <motion.svg
        aria-hidden="true"
        style={{ y: geoY, opacity: geoOpacity }}
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 1600 900"
        fill="none"
        preserveAspectRatio="xMidYMax slice"
      >
        <circle cx="180" cy="980" r="520" stroke="#F4F2EC" strokeOpacity="0.05" strokeWidth="1.5" />
        <circle cx="180" cy="980" r="380" stroke="#F4F2EC" strokeOpacity="0.035" strokeWidth="1.5" />
        <path
          d="M 900 950 Q 1150 620 1620 700"
          stroke="#8FB89E"
          strokeOpacity="0.08"
          strokeWidth="1.5"
        />
        <path
          d="M 700 980 Q 1050 700 1620 780"
          stroke="#F4F2EC"
          strokeOpacity="0.04"
          strokeWidth="1.5"
        />
      </motion.svg>

      <div className="relative px-6 lg:px-10 pt-20 lg:pt-28">
        <p
          data-testid="footer-explore-title"
          className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FB89E]"
        >
          {footerData.exploreTitle}
        </p>
        <div className="mt-8">
          {footerData.exploreLinks.map((link, i) => (
            <motion.a
              key={link.id}
              href={link.href}
              data-testid={`footer-explore-${link.id}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.08 }}
              className="group flex items-center justify-between gap-10 border-t border-white/12 py-6 lg:py-7 transition-colors duration-300 last:border-b hover:border-white/25"
            >
              <div className="flex items-baseline gap-6 lg:gap-10">
                <span className="text-[26px] lg:text-[38px] font-semibold tracking-[-0.025em] leading-none transition-colors duration-300 group-hover:text-[#B9D0C0]">
                  {link.title}
                </span>
                <span className="hidden lg:block text-sm text-white/45 transition-colors duration-300 group-hover:text-white/65">
                  {link.desc}
                </span>
              </div>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/20 transition-all duration-300 group-hover:bg-[var(--strollee-surface)] group-hover:text-[var(--strollee-ink)] group-hover:border-transparent">
                <ArrowUpRight
                  size={17}
                  strokeWidth={2}
                  className="transition-transform duration-300 group-hover:translate-x-[2px] group-hover:-translate-y-[2px]"
                />
              </span>
            </motion.a>
          ))}
        </div>

        <div className="mt-16 lg:mt-24 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <h3
              data-testid="newsletter-title"
              className="text-lg font-semibold tracking-[-0.01em]"
            >
              {footerData.newsletter.title}
            </h3>
            <p className="mt-2 text-sm text-white/50">{footerData.newsletter.copy}</p>
            {joined ? (
              <p
                data-testid="newsletter-success"
                className="mt-5 text-sm font-medium text-[#8FB89E]"
              >
                {footerData.newsletter.success}
              </p>
            ) : (
              <form onSubmit={submit} className="mt-5 flex max-w-[360px] items-center gap-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={footerData.newsletter.placeholder}
                  data-testid="newsletter-input"
                  className="h-11 w-full rounded-full border border-white/15 bg-white/[0.07] px-5 text-sm text-[var(--strollee-bg)] placeholder:text-white/35 outline-none transition-colors duration-200 focus:border-[#8FB89E]/60"
                />
                <button
                  type="submit"
                  data-testid="newsletter-join-btn"
                  className="group inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full bg-[var(--strollee-surface)] px-5 text-[13px] font-semibold text-[var(--strollee-ink)] transition-all duration-200 hover:bg-white"
                >
                  {footerData.newsletter.cta}
                  <ArrowRight
                    size={14}
                    strokeWidth={2.4}
                    className="transition-transform duration-200 group-hover:translate-x-[2px]"
                  />
                </button>
              </form>
            )}
          </div>

          <div className="col-span-12 lg:col-span-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
            {footerData.navGroups.map((group) => (
              <div key={group.title}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
                  {group.title}
                </p>
                <ul className="mt-4 space-y-2.5">
                  {group.links.map((link) => (
                    <li key={link}>
                      <a
                        href="#"
                        data-testid={`footer-nav-${link.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="text-sm text-white/65 transition-colors duration-200 hover:text-white"
                      >
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll-scrubbed product rotation — temporarily hidden, clip ready at
            brandAssets.footerMotion (webm/mp4) */}
        {false && (
          <div className="mt-16 lg:mt-24 flex justify-center">
            <video
              ref={videoRef}
              aria-label={brandAssets.footerMotion.alt}
              data-testid="footer-motion-video"
              muted
              autoPlay
              playsInline
              preload="auto"
              onLoadedMetadata={(e) => {
                durationRef.current = e.currentTarget.duration;
              }}
              onLoadedData={(e) => {
                e.currentTarget.pause();
                e.currentTarget.currentTime = 0.01;
              }}
              className="pointer-events-none h-[300px] lg:h-[360px] w-auto object-contain opacity-[0.85]"
            >
              <source src={brandAssets.footerMotion.webm} type="video/webm" />
              <source src={brandAssets.footerMotion.mp4} type="video/mp4" />
            </video>
          </div>
        )}

        <div className="mt-12 lg:mt-16 overflow-hidden">
          <motion.div
            initial={{ y: 48, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1.3, ease: EASE }}
          >
            <img
              src={brandAssets.logoLight.url}
              alt={brandAssets.logoLight.alt}
              data-testid="footer-wordmark"
              className="w-full select-none opacity-[0.92] transition-all duration-500 hover:opacity-100 hover:[filter:drop-shadow(0_0_28px_rgba(244,242,236,0.22))]"
              draggable={false}
            />
          </motion.div>
        </div>

        <div className="mt-8 lg:mt-10 flex items-center justify-between gap-8 border-t border-white/10 pt-6 pb-8 lg:pb-9">
          <p className="text-[12px] text-white/40">{footerData.copyright}</p>
          <div className="flex items-center gap-6">
            {footerData.legal.map((item) => (
              <a
                key={item}
                href="#"
                data-testid={`footer-legal-${item.toLowerCase()}`}
                className="text-[12px] text-white/40 transition-colors duration-200 hover:text-white/80"
              >
                {item}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};
